import { createSlice } from "@reduxjs/toolkit";

export enum PPAShapeTypes {
	AsForecasted = "AsForecasted",
	AsProduced = "AsProduced",
}

export interface PricingTypeCountry {
	available_ppa_type: PPAShapeTypes[];
	country: { id: number; name: string; iso2_code: string };
	requires_manual_booking: boolean;
	requires_manual_pricing: boolean;
	show_pricing_section_base_option: boolean;
	show_pricing_section_cap_floor: boolean;
	show_pricing_section_cap_floor_intraday: boolean;
	show_pricing_section_chapter_51: boolean;
	show_pricing_section_clickability: boolean;
	show_pricing_section_curtailability_forward: boolean;
	show_pricing_section_negative_price: boolean;
	show_pricing_section_result_options: boolean;
	show_pricing_section_strike_price: boolean;
	show_pricing_section_addons: boolean;
	show_pricing_section_power_goo: boolean;
}

export interface CommodityFixings {
	id: number;
	name: string;
	geco_name?: string;
	external_id: number;
	long_name: string;
	commodity: string;
	market_data_code: string;
	label?: string;
	chapter51_options?: string[];
	has_clickability?: boolean;
	send_constant_click?: boolean;
	direction?: string;
	is_baseprod?: boolean;
	has_result_options?: boolean;
	pricing_type_countries: PricingTypeCountry[];
}

export interface PricingIndexes {
	geco_id: number;
	name: string;
	orchestrade_id: number;
}
export interface ProvisionType {
	id: string;
	name: string;
}
export interface SpotMarket {
	id: string;
	name: string;
}
export interface PricingTemplate {
	id: number;
	title: string;
	params: object;
	label?: string;
}

export interface PricingAddOn {
	id: number;
	name: string;
	code: string;
	geco_id: number;
}
export interface PricingRequestForm {
	loader: {
		commodityFixings: boolean;
		templates: boolean;
		addNewTemplate: boolean;
		pricingIndexes: boolean;
		provisionTypes: boolean;
		pricingAddOns: boolean;
		spotMarkets: boolean;
	};
	errors: {
		commodityFixings?: boolean;
		createPricingRequest?: string;
		templates?: boolean;
		provisionTypes?: string;
		spotMarkets?: string;
		addNewTemplate?: string;
		pricingIndexes?: string;
		pricingAddOns?: string;
	};
	success: {
		addTemplate?: boolean;
	};
	commodityFixings: CommodityFixings[];
	templates: PricingTemplate[];
	pricingIndexes: PricingIndexes[];
	provisionTypes: ProvisionType[];
	pricingAddOns: PricingAddOn[];
	spotMarkets: SpotMarket[];
}

const initialState: PricingRequestForm = {
	loader: {
		commodityFixings: false,
		templates: false,
		addNewTemplate: false,
		pricingIndexes: false,
		provisionTypes: false,
		pricingAddOns: false,
		spotMarkets: false,
	},
	success: {
		addTemplate: false,
	},
	errors: {},
	commodityFixings: [],
	templates: [],
	pricingIndexes: [],
	provisionTypes: [],
	pricingAddOns: [],
	spotMarkets: [],
};

export const pricingRequestFormSlice = createSlice({
	name: "pricingRequestForm",
	initialState,
	reducers: {
		getCommodityFixingsSuccess: (state, action) => {
			state.commodityFixings = action.payload;
			state.loader.commodityFixings = false;
		},
		getCommodityFixingsError: (state, action) => {
			state.errors.commodityFixings = action.payload;
			state.loader.commodityFixings = false;
		},
		createPricingRequestSuccess: (state) => {
			state.errors.createPricingRequest = "";
		},
		createPricingRequestError: (state, action) => {
			state.errors.createPricingRequest = action.payload;
		},
		resetPricingRequestResult: (state) => {
			state.errors.createPricingRequest = "";
		},
		getPricingTemplatesSuccess: (state, action) => {
			state.templates = action.payload;
			state.loader.templates = false;
		},
		getPricingTemplateError: (state, action) => {
			state.errors.templates = action.payload;
			state.loader.templates = false;
		},
		getProvisionTypesSuccess: (state, action) => {
			state.provisionTypes = action.payload;
			state.loader.provisionTypes = false;
		},
		getProvisionTypesError: (state, action) => {
			state.errors.provisionTypes = action.payload;
			state.loader.provisionTypes = false;
		},
		getSpotMarketsSuccess: (state, action) => {
			state.spotMarkets = action.payload;
			state.loader.spotMarkets = false;
		},
		getSpotMarketsError: (state, action) => {
			state.errors.spotMarkets = action.payload;
			state.loader.spotMarkets = false;
		},
		enablePricingTemplatesLoader: (state) => {
			state.loader.addNewTemplate = true;
		},
		addNewPricingTemplates: (state, action) => {
			state.templates = [...state.templates, action.payload];
			state.errors.addNewTemplate = "";
			state.loader.addNewTemplate = false;
			state.success.addTemplate = true;
		},
		addNewPricingTemplatesErrors: (state, action) => {
			state.errors.addNewTemplate = action.payload;
			state.loader.addNewTemplate = false;
			state.success.addTemplate = false;
		},
		getPricingIndexesSuccess: (state, action) => {
			state.pricingIndexes = action.payload;
			state.loader.pricingIndexes = false;
		},
		getPricingIndexesError: (state, action) => {
			state.errors.pricingIndexes = action.payload;
			state.loader.pricingIndexes = false;
		},
		getPricingAddOnsSuccess: (state, action) => {
			state.pricingAddOns = action.payload;
			state.loader.pricingAddOns = false;
		},
		getPricingAddOnsError: (state, action) => {
			state.errors.pricingAddOns = action.payload;
			state.loader.pricingAddOns = false;
		},
	},
});

export const {
	getCommodityFixingsSuccess,
	getCommodityFixingsError,
	createPricingRequestSuccess,
	resetPricingRequestResult,
	createPricingRequestError,
	getPricingTemplatesSuccess,
	getPricingTemplateError,
	addNewPricingTemplates,
	addNewPricingTemplatesErrors,
	enablePricingTemplatesLoader,
	getPricingIndexesSuccess,
	getPricingIndexesError,
	getPricingAddOnsSuccess,
	getPricingAddOnsError,
	getProvisionTypesSuccess,
	getProvisionTypesError,
	getSpotMarketsSuccess,
	getSpotMarketsError,
} = pricingRequestFormSlice.actions;

export default pricingRequestFormSlice.reducer;
